import { FC } from "react";
import { Box, Switch, Typography } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
  DayOfWeek,
  DayScheduleItem,
  SiteOfficeHoursSchedule,
} from "@/domain/site-office-hours.interface";
import { SkeletonWeeklySchedule } from "./skeletons";
import Button from "@/components/Button";

const DAYS_ORDER: DayOfWeek[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const DAY_LABELS: Record<DayOfWeek, string> = {
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
  SUNDAY: "Sunday",
};

export type DayState = {
  dayOfWeek: DayOfWeek;
  enabled: boolean;
  openTime: string;
  closeTime: string;
  openTimeError?: string;
  closeTimeError?: string;
};

export const buildDefaultSchedule = (): DayState[] =>
  DAYS_ORDER.map((day) => ({
    dayOfWeek: day,
    enabled: false,
    openTime: "09:00",
    closeTime: "09:00",
  }));

export const buildScheduleFromApi = (days: DayScheduleItem[]): DayState[] => {
  const dayMap = new Map(days.map((d) => [d.dayOfWeek, d]));
  return DAYS_ORDER.map((day) => {
    const apiDay = dayMap.get(day);
    return {
      dayOfWeek: day,
      enabled: apiDay?.enabled ?? false,
      openTime: apiDay?.openTime ?? "09:00",
      closeTime: apiDay?.closeTime ?? "09:00",
    };
  });
};

const toTimeString = (value: Dayjs | null): string =>
  value?.isValid() ? value.format("HH:mm") : "";

const toTimeDayjs = (value: string): Dayjs | null =>
  value ? dayjs(`2000-01-01T${value}`) : null;

type Props = {
  schedule: DayState[];
  onChange: (updated: DayState[]) => void;
  onSave: () => void;
  isLoading: boolean;
  isSaving: boolean;
  apiSchedule: SiteOfficeHoursSchedule | null | undefined;
  canSave: boolean;
};

export const WeeklySchedule: FC<Props> = ({
  schedule,
  onChange,
  onSave,
  isLoading,
  isSaving,
  canSave,
}) => {
  const updateDay = (index: number, patch: Partial<DayState>) => {
    const updated = schedule.map((day, i) =>
      i === index ? { ...day, ...patch } : day
    );
    onChange(updated);
  };

  const validateAndSave = () => {
    let hasError = false;
    const validated = schedule.map((day) => {
      if (!day.enabled)
        return { ...day, openTimeError: undefined, closeTimeError: undefined };

      let openTimeError: string | undefined;
      let closeTimeError: string | undefined;

      if (!day.openTime) {
        openTimeError = "Required";
        hasError = true;
      }
      if (!day.closeTime) {
        closeTimeError = "Required";
        hasError = true;
      }
      if (day.openTime && day.closeTime && day.closeTime <= day.openTime) {
        closeTimeError = "Must be after open time";
        hasError = true;
      }

      return { ...day, openTimeError, closeTimeError };
    });

    onChange(validated);
    if (!hasError) {
      onSave();
    }
  };

  if (isLoading) {
    return <SkeletonWeeklySchedule />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Typography variant="bodyLarge" color="primary" fontWeight={700}>
          Weekly Schedule
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            mt: 2,
          }}
        >
          {schedule.map((day, index) => (
            <Box
              key={day.dayOfWeek}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px",
                p: "0.75rem 1rem",
                bgcolor: "background.paper",
              }}
            >
              <Typography
                variant="bodyLarge"
                sx={{ minWidth: 100, fontWeight: 500 }}
              >
                {DAY_LABELS[day.dayOfWeek]}
              </Typography>

              <Switch
                checked={day.enabled}
                onChange={(e) =>
                  updateDay(index, { enabled: e.target.checked })
                }
                color="primary"
                size="small"
              />

              <TimePicker
                label="Open"
                value={toTimeDayjs(day.openTime)}
                onChange={(val) =>
                  updateDay(index, {
                    openTime: toTimeString(val),
                    openTimeError: undefined,
                  })
                }
                disabled={!day.enabled}
                ampm
                slotProps={{
                  textField: {
                    size: "small",
                    error: !!day.openTimeError,
                    helperText: day.openTimeError,
                    sx: { minWidth: 155 },
                  },
                }}
              />

              <Typography variant="bodyMedium" color="text.secondary">
                -
              </Typography>

              <TimePicker
                label="Close"
                value={toTimeDayjs(day.closeTime)}
                onChange={(val) =>
                  updateDay(index, {
                    closeTime: toTimeString(val),
                    closeTimeError: undefined,
                  })
                }
                disabled={!day.enabled}
                ampm
                slotProps={{
                  textField: {
                    size: "small",
                    error: !!day.closeTimeError,
                    helperText: day.closeTimeError,
                    sx: { minWidth: 155 },
                  },
                }}
              />
            </Box>
          ))}
        </Box>

        <Box mt={3}>
          <Button
            variantType="save"
            onClick={validateAndSave}
            disabled={!canSave || isSaving}
            isLoading={isSaving}
            label={isSaving ? "Saving..." : "SAVE"}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};
